import React from "react";
import { Button, FormControl } from "@material-ui/core";
import { KeyboardDatePicker } from "@material-ui/pickers";
import { Controller, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import moment from "moment";
import constants from "@tracking/utils/constants";

const { DATE_FORMAT } = constants;

const DEFAULT_DAYS_RANGE = 7;
const MAX_DAYS_RANGE = 7;

const getDefaultPeriod = () => ({
  dataInicial: moment()
    .subtract(DEFAULT_DAYS_RANGE, "days")
    .format(DATE_FORMAT.API),
  dataFinal: moment().format(DATE_FORMAT.API),
});

export interface RelatorioPeriodFilters {
  dataInicial: string;
  dataFinal: string;
}

interface FilterFormValues {
  dataInicial: string;
  dataFinal: string;
}

interface RelatorioFiltersProps {
  onFilter: (period: RelatorioPeriodFilters) => void;
}

const defaultValues: FilterFormValues = getDefaultPeriod();

const isPeriodWithinMaxRange = (dataInicial: string, dataFinal: string) => {
  const diff = moment(dataFinal, DATE_FORMAT.API).diff(
    moment(dataInicial, DATE_FORMAT.API),
    "days"
  );
  return diff >= 0 && diff <= MAX_DAYS_RANGE;
};

const schema = yup.object().shape({
  dataInicial: yup
    .string()
    .required("Data inicial é obrigatória")
    .test(
      "intervalo-maximo",
      "O período não pode ser maior que 7 dias",
      function (value) {
        const { dataFinal } = this.parent;
        if (!value || !dataFinal) return true;
        return isPeriodWithinMaxRange(value, dataFinal);
      }
    ),
  dataFinal: yup
    .string()
    .required("Data final é obrigatória")
    .test(
      "data-final",
      "Data inicial não pode ser maior que a data final",
      function (value) {
        const { dataInicial } = this.parent;
        if (!dataInicial || !value) return true;
        return !moment(dataInicial, DATE_FORMAT.API).isAfter(
          moment(value, DATE_FORMAT.API)
        );
      }
    )
    .test(
      "intervalo-maximo",
      "O período não pode ser maior que 7 dias",
      function (value) {
        const { dataInicial } = this.parent;
        if (!dataInicial || !value) return true;
        return isPeriodWithinMaxRange(dataInicial, value);
      }
    ),
});

const parseSubmit = (data: FilterFormValues): RelatorioPeriodFilters => ({
  dataInicial: moment(data.dataInicial, DATE_FORMAT.API).format(
    DATE_FORMAT.API
  ),
  dataFinal: moment(data.dataFinal, DATE_FORMAT.API).format(DATE_FORMAT.API),
});

const RelatorioFilters: React.FC<RelatorioFiltersProps> = ({ onFilter }) => {
  const {
    control,
    handleSubmit,
    reset,
    trigger,
    formState: { errors },
  } = useForm<FilterFormValues>({
    defaultValues,
    resolver: yupResolver(schema),
    mode: "onChange",
  });

  const submitForm = (data: FilterFormValues) => {
    onFilter(parseSubmit(data));
  };

  const handleLimpar = () => {
    const period = getDefaultPeriod();
    reset(period);
    onFilter(period);
  };

  return (
    <div className="grid xl:grid-cols-1 lg:grid-cols-1 bg-white p-[18px_24px] shadow-[0px_3px_4px_#DBDCDC] rounded-md items-center relative">
      <form onSubmit={handleSubmit(submitForm)} className="gap-y-2 grid">
        <div className="flex gap-4 items-center flex-wrap">
          <FormControl
            className="min-w-[220px]"
            variant="outlined"
            error={!!errors.dataInicial}
          >
            <Controller
              control={control}
              name="dataInicial"
              render={({ field: { onChange, value } }) => (
                <KeyboardDatePicker
                  label="Selecione data inicial"
                  value={value || null}
                  onChange={(newValue) => {
                    onChange(
                      newValue ? moment(newValue).format(DATE_FORMAT.API) : ""
                    );
                    trigger("dataFinal");
                  }}
                  format={DATE_FORMAT.FIELD}
                  inputVariant="outlined"
                  size="small"
                  KeyboardButtonProps={{
                    "aria-label": "change date",
                  }}
                  cancelLabel="Cancelar"
                  clearLabel="Limpar"
                  invalidDateMessage="Data inválida"
                  error={!!errors.dataInicial}
                  helperText={errors.dataInicial?.message}
                />
              )}
            />
          </FormControl>

          <FormControl
            className="min-w-[220px]"
            variant="outlined"
            error={!!errors.dataFinal}
          >
            <Controller
              control={control}
              name="dataFinal"
              render={({ field: { onChange, value } }) => (
                <KeyboardDatePicker
                  label="Selecione data final"
                  value={value || null}
                  onChange={(newValue) => {
                    onChange(
                      newValue ? moment(newValue).format(DATE_FORMAT.API) : ""
                    );
                    trigger("dataInicial");
                  }}
                  format={DATE_FORMAT.FIELD}
                  inputVariant="outlined"
                  size="small"
                  KeyboardButtonProps={{
                    "aria-label": "change date",
                  }}
                  cancelLabel="Cancelar"
                  clearLabel="Limpar"
                  invalidDateMessage="Data inválida"
                  error={!!errors.dataFinal}
                  helperText={errors.dataFinal?.message}
                />
              )}
            />
          </FormControl>

          <div className="flex gap-2 ml-auto justify-end">
            <Button
              type="submit"
              color="primary"
              variant="contained"
              disableElevation
            >
              FILTRAR
            </Button>
            <Button
              type="button"
              variant="contained"
              onClick={handleLimpar}
              disableElevation
            >
              LIMPAR
            </Button>
          </div>
        </div>
      </form>
    </div>
  );
};

export const formatAppliedPeriodLabel = (period: RelatorioPeriodFilters) =>
  `Período: ${moment(period.dataInicial, DATE_FORMAT.API).format(
    DATE_FORMAT.FIELD
  )} a ${moment(period.dataFinal, DATE_FORMAT.API).format(DATE_FORMAT.FIELD)}`;

export { getDefaultPeriod };

export default RelatorioFilters;
